import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  BaseInput,
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { Modal } from "shared/components/Modal";
import { Applicant } from "types/application";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { moneyCentsParser } from "shared/components/forms/BaseInput/parsers";
import { useFormTyped } from "shared/hooks";
import { getFullName } from "shared/utils/get-full-name";
import { getApplicantRole } from "utils/getApplicantRole";
import { getIncomeJobName, incomeTypeMap } from "../utils";
import { CombinedIncome } from "../types/income";
import { IncomeType } from "shared/types";
import * as Yup from "yup";

const fieldValidation = Yup.number().required("Value is required");

const validationSchema = Yup.object().shape({
  gross: fieldValidation,
  net: fieldValidation
});

export const VerificationOverrideModal = ({
  opened,
  onClose,
  applicant,
  verificationId,
  applicationId,
  onSuccess,
  id: incomeId,
  additional_data: additionalData,
  type: incomeType
}: {
  opened: boolean;
  onClose: () => void;
  applicant: Applicant;
  verificationId: number;
  applicationId: number;
  onSuccess: () => void;
  id?: number;
  additional_data?: CombinedIncome["additional_data"];
  type?: IncomeType;
}) => {
  const queryClient = useQueryClient();
  // Both fields start empty rather than at 0: Yup.number().required() passes on 0, so defaulting to
  // 0 let an untouched form submit and record no income for this source. null fails validation, so
  // recording $0 has to be typed out.
  const form = useFormTyped<{ gross: number | null; net: number | null }>(
    {
      gross: null,
      net: null
    },
    validationSchema
  );

  const mutation = useMutation({
    mutationKey: [
      "OVERRIDE_INCOME",
      applicationId,
      verificationId,
      applicant.id,
      incomeId
    ],
    mutationFn: async (data: { gross: number; net: number }) => {
      // Update income amounts
      await instance.post(
        `portal/v1/applications/${applicationId}/verifications/${verificationId}/create_override/income/${incomeId}?applicant_id=${applicant.id}`,
        {
          gross_monthly_income: data.gross / 100,
          net_monthly_income: data.net / 100
        }
      );
      // Set override status
      const { data: result } = await instance.post(
        `portal/v1/applications/${applicationId}/verifications/${verificationId}/income/${incomeId}/finish_with_override?applicant_id=${applicant.id}`,
        {}
      );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      onSuccess();
    },
    onError: (err: Error) => {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-md w-full relative flex flex-col"
    >
      <form
        onSubmit={form.onSubmit({
          onSuccess: ({ gross, net }) => {
            // Narrowing only — validationSchema has already rejected empty fields.
            if (gross === null || net === null) return;

            mutation.mutate({ gross, net });
          }
        })}
      >
        <header className="px-6 py-5 text-lg font-bold flex justify-between items-center border-b border-alpha-100">
          Complete verification of the current income source
          <IconButton
            HugeIcon={Cancel01Icon}
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={onClose}
            className="ml-4"
          />
        </header>
        <div className="px-6 py-5 border-b border-alpha-100 flex items-center justify-between bg-white">
          <div className="space-y-1">
            <h2 className="text-base font-medium text-content-1000">
              {getFullName({
                firstName: applicant.first_name,
                lastName: applicant.last_name,
                middleName: applicant.middle_name,
                short: true
              })}
            </h2>
            <p className="text-sm text-content-400 font-medium">
              {getApplicantRole(applicant.role)}
            </p>
          </div>

          <div className="space-y-1 text-right">
            <h2 className="text-base font-medium text-content-1000">
              {getIncomeJobName(additionalData)}
            </h2>
            <p className="text-sm text-content-400 font-medium">
              {incomeType && incomeTypeMap[incomeType]}
            </p>
          </div>
        </div>

        <fieldset className="bg-background-900 p-6">
          <BaseInput
            id="gross"
            label="Verified gross monthly income"
            prefix="$"
            placeholder="0.00"
            hideOptional
            parser={moneyCentsParser()}
            {...form.register("gross")}
          />

          <BaseInput
            id="net"
            label="Verified net monthly income"
            prefix="$"
            placeholder="0.00"
            hideOptional
            parser={moneyCentsParser()}
            {...form.register("net")}
            className="mt-2"
          />
        </fieldset>

        <div className="px-6 py-4 flex gap-2 justify-end border-t border-alpha-100">
          <Button className="w-[125px]" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant={ButtonVariant.PRIMARY}
            type="submit"
            loading={mutation.isPending}
          >
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};
